import { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'lib/Auth';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { GroupedEmailPermissions, EmailPermission } from 'data/EmailPermissions';
import { getEmailPermissionsByRole } from 'requests/emailPermissions';

interface EmailSettingsHookProps {
    roleId: number;
    setEditedPermissions: React.Dispatch<React.SetStateAction<EmailPermission[]>>;
    setHasUnsavedChanges: (value: boolean) => void;
}

interface EmailSettingsReturn {
    // Data state
    permissions: GroupedEmailPermissions[];
    loading: boolean;

    // CRUD actions
    handleEdit: (permission: EmailPermission, active: boolean) => void;

    // Utility actions
    refreshData: () => void;
    resetState: () => void;
}

export const useEmailSettings = ({
    roleId,
    setEditedPermissions,
    setHasUnsavedChanges
}: EmailSettingsHookProps): EmailSettingsReturn => {
    const { token } = useAuth();

    // Data state
    const [permissions, setPermissions] = useState<GroupedEmailPermissions[]>([]);
    const [originalPermissions, setOriginalPermissions] = useState<GroupedEmailPermissions[]>([]);
    const [loading, setLoading] = useState(false);

    // Helper function to create a deep copy of permissions
    const deepCopyPermissions = (perms: GroupedEmailPermissions[]): GroupedEmailPermissions[] => {
        return perms.map(group => ({
            ...group,
            data: group.data.map(permission => ({ ...permission }))
        }));
    };

    // Helper function to check if permissions have changed
    const hasPermissionsChanged = useCallback((current: GroupedEmailPermissions[], original: GroupedEmailPermissions[]): boolean => {
        return JSON.stringify(current) !== JSON.stringify(original);
    }, []);

    // Reset all state to initial values
    const resetState = useCallback(() => {
        setPermissions([]);
        setOriginalPermissions([]);
        setLoading(false);
    }, []);

    // Fetch email permissions
    const fetchEmailPermissions = useCallback(async (showLoading = true) => {
        if (!roleId || !token) {
            setPermissions([]);
            setOriginalPermissions([]);
            return;
        }

        try {
            if (showLoading) {
                setLoading(true);
            }

            console.log('Fetching email permissions with params:', {
                roleId,
            });

            const response = await getEmailPermissionsByRole(roleId, { token });

            if (response && Array.isArray(response)) {
                console.log('Successfully fetched email permissions:', {
                    roleId,
                    dataLength: response.length
                });

                const permissionsData = response as GroupedEmailPermissions[];
                setPermissions(permissionsData);

                // Store original permissions for comparison
                setOriginalPermissions(deepCopyPermissions(permissionsData));

                // Reset edited permissions and unsaved changes when new data loads
                setEditedPermissions([]);
                setHasUnsavedChanges(false);
            } else {
                console.warn('Unexpected response structure:', response);
            }

        } catch (err: any) {
            console.error('Error fetching email permissions:', err);
            renderToast(toast.TYPE.ERROR, err.message);
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    }, [roleId, token, setEditedPermissions, setHasUnsavedChanges]);

    // Initial load and dependency-based reload
    useEffect(() => {
        fetchEmailPermissions();
    }, [fetchEmailPermissions]);

    // CRUD action handlers
    const handleEdit = useCallback(
        (permission: EmailPermission, active: boolean) => {
            const updatedPermission: EmailPermission = {
                ...permission,
                active
            };

            // Update edited permissions array
            setEditedPermissions((prev: EmailPermission[]) => {
                const exists = prev.find((p) => p.id === permission.id);
                return exists
                    ? prev.map((p) => (p.id === permission.id ? updatedPermission : p))
                    : [...prev, updatedPermission];
            });

            // Update current permissions state
            const updatedPermissions = permissions.map((grouped) => ({
                ...grouped,
                data: grouped.data.map((p) =>
                    p.id === permission.id
                        ? { ...p, active }
                        : p
                ),
            }));

            setPermissions(updatedPermissions);

            // Check if there are unsaved changes by comparing with original
            const hasChanges = hasPermissionsChanged(updatedPermissions, originalPermissions);
            setHasUnsavedChanges(hasChanges);
        },
        [permissions, originalPermissions, hasPermissionsChanged, setEditedPermissions, setHasUnsavedChanges]
    );

    // Utility actions
    const refreshData = useCallback(() => {
        fetchEmailPermissions(true);
    }, [fetchEmailPermissions]);

    return {
        // Data state
        permissions,
        loading,

        // CRUD actions
        handleEdit,

        // Utility actions
        refreshData,
        resetState
    };
};
